/**
 * Shared student fee / payment state.
 *
 * Server per-enrollment ledger (owedTotal / creditTotal / balance):
 * - owedTotal = money still to collect (no cross-course prepaid netting)
 * - creditTotal = prepaid credit on future months
 * - balance display = +owed if any owed, else -credit if any credit, else 0
 *
 * Student action rule (Home, My Courses, Checkout):
 * 1) Pending enrollments → payment action required (proof upload → enrolled)
 * 2) Else any course owes through today (hasCourseOwed / owedTotal > 0) → prompt
 * 3) Else clear (FOC unpaid @ 0, pure prepaid credit)
 */

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function parseFeeAmount(value) {
  const raw = String(value ?? "0").replace(/[^0-9-]/g, "");
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function parseDiscountAmount(value) {
  const parsed = parseFloat(String(value ?? "0").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Catalog monthly fee net of PKR discount (same as admin table). */
export function netCourseFee(course) {
  const fee = parseFeeAmount(course?.fee);
  const discount = parseDiscountAmount(course?.discount);
  return Math.max(0, Math.round(fee - discount));
}

export function formatPkrAmount(amount) {
  const n = Math.round(Number(amount) || 0);
  return `PKR ${n.toLocaleString()}`;
}

export function formatPaymentStatusLabel(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "partially_paid") return "Partially Paid";
  if (normalized === "partial") return "Partial";
  if (normalized === "unpaid") return "Unpaid";
  if (normalized === "paid") return "Paid";
  if (normalized === "waived") return "Waived";
  if (normalized === "pending") return "Pending enrollment";
  if (!normalized) return "—";
  return status;
}

export function monthYearLabel(month, year) {
  const idx = Number(month) - 1;
  const name = MONTH_NAMES[idx] || `Month ${month}`;
  return `${name} ${year}`;
}

export function getPendingCourses(courses = []) {
  return (courses || []).filter(
    (course) => String(course?.status || "").toLowerCase() === "pending",
  );
}

export function getEnrolledCourses(courses = []) {
  return (courses || []).filter(
    (course) => String(course?.status || "").toLowerCase() === "enrolled",
  );
}

export function getActiveCourses(courses = []) {
  return (courses || []).filter((course) =>
    ["enrolled", "pending"].includes(
      String(course?.status || "").toLowerCase(),
    ),
  );
}

/** Matches server summarizePeriods waived handling. */
export function periodEffectiveDue(period) {
  if (String(period?.status || "").toLowerCase() === "waived") {
    return Number(period.amountPaid) || 0;
  }
  return Number(period?.amountDue) || 0;
}

export function periodRemaining(period) {
  if (String(period?.status || "").toLowerCase() === "waived") return 0;
  return Math.max(
    0,
    (Number(period?.amountDue) || 0) - (Number(period?.amountPaid) || 0),
  );
}

/**
 * Month rows that still have remaining due (optionally for one course).
 */
export function buildDueMonthSplit(feePeriods = [], courseId = null) {
  const byMonth = new Map();
  const courseKey = courseId == null ? null : String(courseId);

  for (const period of feePeriods || []) {
    if (courseKey != null && String(period?.courseId) !== courseKey) continue;
    const remaining = periodRemaining(period);
    if (remaining <= 0) continue;
    const month = Number(period.month);
    const year = Number(period.year);
    if (!month || !year) continue;
    const key = `${year}-${String(month).padStart(2, "0")}`;
    const existing = byMonth.get(key) || {
      key,
      month,
      year,
      label: monthYearLabel(month, year),
      charged: 0,
      paid: 0,
      remaining: 0,
    };
    existing.charged += periodEffectiveDue(period);
    existing.paid += Number(period.amountPaid) || 0;
    existing.remaining += remaining;
    byMonth.set(key, existing);
  }

  return [...byMonth.values()]
    .map((row) => ({
      ...row,
      charged: Math.round(row.charged),
      paid: Math.round(row.paid),
      remaining: Math.round(row.remaining),
    }))
    .sort((a, b) => a.year - b.year || a.month - b.month);
}

/**
 * Due-month breakdown that sums to payable (feeOwed / owedTotal).
 * Allocates each course's net owed across its open periods earliest-first,
 * so prepaid future months do not inflate the month list vs the total.
 */
export function buildPayableDueMonths(registration, feeCourses = []) {
  const byMonth = new Map();
  const periods = [...(registration?.feePeriods || [])].sort(
    (a, b) =>
      Number(a.year) - Number(b.year) || Number(a.month) - Number(b.month),
  );

  for (const course of feeCourses || []) {
    let remainingOwed = Math.round(Number(course.chargedRemaining) || 0);
    if (remainingOwed <= 0) continue;
    const courseKey = String(course.id);

    for (const period of periods) {
      if (remainingOwed <= 0) break;
      if (String(period?.courseId) !== courseKey) continue;
      const open = periodRemaining(period);
      if (open <= 0) continue;
      const alloc = Math.min(open, remainingOwed);
      const month = Number(period.month);
      const year = Number(period.year);
      if (!month || !year) continue;
      const key = `${year}-${String(month).padStart(2, "0")}`;
      const existing = byMonth.get(key) || {
        key,
        month,
        year,
        label: monthYearLabel(month, year),
        remaining: 0,
      };
      existing.remaining += alloc;
      byMonth.set(key, existing);
      remainingOwed -= alloc;
    }

    // Pending catalog with no periods yet — put owed on a synthetic "due now" row.
    if (remainingOwed > 0) {
      const key = `catalog-${courseKey}`;
      const existing = byMonth.get(key) || {
        key,
        month: 0,
        year: 0,
        label: course.title || "Course fee",
        remaining: 0,
      };
      existing.remaining += remainingOwed;
      byMonth.set(key, existing);
    }
  }

  return [...byMonth.values()]
    .map((row) => ({
      ...row,
      remaining: Math.round(row.remaining),
    }))
    .sort(
      (a, b) =>
        (a.year || 9999) - (b.year || 9999) ||
        (a.month || 99) - (b.month || 99),
    );
}

/**
 * Courses currently payable. Prefer server per-enrollment feeOwed
 * (same-course prepaid already netted). Fall back to period remainders /
 * net catalog fee only when feeOwed is absent.
 */
export function getChargedCourses(registration) {
  const courses = registration?.courses || [];
  const active = getActiveCourses(courses);
  const activeById = new Map(
    active.map((course) => [String(course.id), course]),
  );
  const grossByCourse = new Map();
  const monthsByCourse = new Map();

  for (const period of registration?.feePeriods || []) {
    const courseKey = period?.courseId == null ? null : String(period.courseId);
    if (!courseKey || !activeById.has(courseKey)) continue;
    const remaining = periodRemaining(period);
    if (remaining <= 0) continue;
    grossByCourse.set(
      courseKey,
      (grossByCourse.get(courseKey) || 0) + remaining,
    );
  }

  for (const courseKey of grossByCourse.keys()) {
    monthsByCourse.set(
      courseKey,
      buildDueMonthSplit(registration?.feePeriods, courseKey),
    );
  }

  return active
    .map((course) => {
      const courseKey = String(course.id);
      let chargedRemaining = null;

      if (course.feeOwed != null) {
        chargedRemaining = Math.round(Number(course.feeOwed) || 0);
      } else if (grossByCourse.has(courseKey)) {
        chargedRemaining = Math.round(grossByCourse.get(courseKey));
      } else if (String(course.status || "").toLowerCase() === "pending") {
        chargedRemaining = netCourseFee(course);
      } else {
        chargedRemaining = 0;
      }

      return {
        ...course,
        chargedRemaining,
        monthCharges: monthsByCourse.get(courseKey) || [],
      };
    })
    .filter((course) => Number(course.chargedRemaining) > 0);
}

function rowPaymentStatus(period, charged, paid, remaining) {
  const status = String(period?.status || "").toLowerCase();
  if (status === "waived") return "waived";
  if (remaining <= 0) return "paid";
  if (paid <= 0) return "unpaid";
  return "partial";
}

/**
 * Course × month history rows (API already limits periods to through
 * current month). One row per fee period / course-month.
 */
export function buildFeeCourseMonthHistory(feePeriods = [], courses = []) {
  const courseById = new Map(
    (courses || []).map((course) => [String(course.id), course]),
  );
  const rows = [];

  for (const period of feePeriods || []) {
    const month = Number(period.month);
    const year = Number(period.year);
    if (!month || !year) continue;

    const courseId = period.courseId;
    const course = courseById.get(String(courseId));
    const charged = Math.round(periodEffectiveDue(period));
    const paid = Math.round(Number(period.amountPaid) || 0);
    const remaining = Math.round(periodRemaining(period));

    rows.push({
      key: `${courseId ?? "x"}-${year}-${String(month).padStart(2, "0")}-${
        period.id ?? rows.length
      }`,
      courseId,
      courseTitle: course?.title || (courseId != null ? `Course ${courseId}` : "Course"),
      month,
      year,
      monthLabel: monthYearLabel(month, year),
      charged,
      paid,
      remaining,
      status: rowPaymentStatus(period, charged, paid, remaining),
    });
  }

  rows.sort(
    (a, b) =>
      String(a.courseTitle).localeCompare(String(b.courseTitle)) ||
      a.year - b.year ||
      a.month - b.month,
  );

  const groupsByCourse = new Map();
  for (const row of rows) {
    const groupKey = String(row.courseId ?? row.courseTitle);
    const existing = groupsByCourse.get(groupKey) || {
      key: groupKey,
      courseId: row.courseId,
      courseTitle: row.courseTitle,
      months: [],
      charged: 0,
      paid: 0,
      remaining: 0,
    };
    existing.months.push(row);
    existing.charged += row.charged;
    existing.paid += row.paid;
    existing.remaining += row.remaining;
    groupsByCourse.set(groupKey, existing);
  }

  const groups = [...groupsByCourse.values()].map((group) => ({
    ...group,
    months: [...group.months].sort(
      (a, b) => a.year - b.year || a.month - b.month,
    ),
  }));

  const totals = rows.reduce(
    (acc, row) => {
      acc.charged += row.charged;
      acc.paid += row.paid;
      acc.remaining += row.remaining;
      return acc;
    },
    { charged: 0, paid: 0, remaining: 0 },
  );

  return { rows, groups, totals };
}

/**
 * Month-only rollup (due-by-month style summaries).
 */
export function buildFeeMonthProjection(feePeriods = []) {
  const byMonth = new Map();

  for (const period of feePeriods || []) {
    const month = Number(period.month);
    const year = Number(period.year);
    if (!month || !year) continue;
    const key = `${year}-${String(month).padStart(2, "0")}`;
    const charged = periodEffectiveDue(period);
    const paid = Number(period.amountPaid) || 0;
    const remaining = periodRemaining(period);
    const existing = byMonth.get(key) || {
      key,
      month,
      year,
      label: monthYearLabel(month, year),
      charged: 0,
      paid: 0,
      remaining: 0,
    };
    existing.charged += charged;
    existing.paid += paid;
    existing.remaining += remaining;
    byMonth.set(key, existing);
  }

  const months = [...byMonth.values()]
    .map((row) => {
      const charged = Math.round(row.charged);
      const paid = Math.round(row.paid);
      const remaining = Math.round(row.remaining);
      return {
        ...row,
        charged,
        paid,
        remaining,
        status:
          remaining <= 0
            ? "paid"
            : paid <= 0
              ? "unpaid"
              : "partial",
      };
    })
    .sort((a, b) => a.year - b.year || a.month - b.month);

  const totals = months.reduce(
    (acc, row) => {
      acc.charged += row.charged;
      acc.paid += row.paid;
      acc.remaining += row.remaining;
      return acc;
    },
    { charged: 0, paid: 0, remaining: 0 },
  );

  return { months, totals };
}

function getLedgerState(registration) {
  const ledgerBalance = parseFeeAmount(registration?.balance);
  const ledgerStatus = String(registration?.paymentstatus || "").toLowerCase();
  const owedTotal =
    registration?.owedTotal != null
      ? Number(registration.owedTotal) || 0
      : Math.max(0, ledgerBalance);
  const creditTotal =
    registration?.creditTotal != null
      ? Number(registration.creditTotal) || 0
      : ledgerBalance < 0
        ? Math.abs(ledgerBalance)
        : 0;
  const hasCourseOwed =
    registration?.hasCourseOwed != null
      ? Boolean(registration.hasCourseOwed)
      : owedTotal > 0;

  // Any course still owed through today — never masked by another course's prepaid.
  const ledgerNeedsPayment = hasCourseOwed;

  return {
    ledgerBalance,
    ledgerStatus: ledgerStatus || "paid",
    ledgerNeedsPayment,
    owedTotal,
    creditTotal,
    hasCourseOwed,
    ledgerLabel: formatPaymentStatusLabel(ledgerStatus || "paid"),
    ledgerOutstandingLabel:
      owedTotal > 0
        ? formatPkrAmount(owedTotal)
        : registration?.balance || formatPkrAmount(ledgerBalance),
  };
}

export function resolveStudentFeeState(registration) {
  const pendingCourses = getPendingCourses(registration?.courses);
  const hasPendingEnrollment = pendingCourses.length > 0;
  const {
    ledgerBalance,
    ledgerStatus,
    ledgerNeedsPayment,
    owedTotal,
    creditTotal,
    hasCourseOwed,
    ledgerLabel,
    ledgerOutstandingLabel,
  } = getLedgerState(registration);

  const monthProjection = buildFeeMonthProjection(registration?.feePeriods);
  const courseMonthHistory = buildFeeCourseMonthHistory(
    registration?.feePeriods,
    registration?.courses,
  );
  const feeCourses = getChargedCourses(registration);
  const feeCoursesSum = feeCourses.reduce(
    (sum, course) => sum + (Number(course.chargedRemaining) || 0),
    0,
  );

  // Payable total = server owedTotal when present; else sum of charged courses
  // (pending catalog / legacy). Never prefer gross remainders over owedTotal.
  const outstanding = owedTotal > 0 ? owedTotal : 0;
  const outstandingLabel =
    owedTotal > 0 ? ledgerOutstandingLabel : "PKR 0";
  const dueMonths = buildPayableDueMonths(registration, feeCourses);

  const base = {
    monthProjection,
    months: monthProjection.months,
    historyRows: courseMonthHistory.rows,
    historyGroups: courseMonthHistory.groups,
    totals: courseMonthHistory.totals,
    dueMonths,
    feeCourses,
    chargedCoursesTotal: outstanding > 0 ? outstanding : feeCoursesSum,
    owedTotal,
    creditTotal,
    hasCourseOwed,
  };

  if (!hasPendingEnrollment && !ledgerNeedsPayment) {
    return {
      needsPayment: false,
      paymentStatus: "paid",
      paymentStatusLabel:
        creditTotal > 0 ? "Prepaid credit" : "Paid",
      outstanding: 0,
      outstandingLabel:
        creditTotal > 0 ? formatPkrAmount(-creditTotal) : "PKR 0",
      source: creditTotal > 0 ? "credit" : "clear",
      ledgerStatus,
      ledgerBalance,
      ...base,
      feeCourses: [],
      dueMonths: [],
      chargedCoursesTotal: 0,
    };
  }

  if (ledgerNeedsPayment) {
    return {
      needsPayment: true,
      // Pending enrollment wins the chip when proof upload is still required.
      paymentStatus: hasPendingEnrollment ? "pending" : ledgerStatus,
      paymentStatusLabel: hasPendingEnrollment
        ? "Pending enrollment"
        : ledgerLabel,
      outstanding,
      outstandingLabel,
      source: hasPendingEnrollment ? "pending_and_ledger" : "ledger",
      ledgerStatus,
      ledgerBalance,
      ...base,
      chargedCoursesTotal: outstanding,
    };
  }

  // Pending enrollment only — proof upload path, not money debt.
  const pendingCatalogTotal = pendingCourses.reduce(
    (sum, course) => sum + netCourseFee(course),
    0,
  );
  const pendingFeeCourses = getPendingCourses(registration?.courses).map(
    (course) => ({
      ...course,
      chargedRemaining: netCourseFee(course),
      monthCharges: [],
    }),
  ).filter((course) => course.chargedRemaining > 0);

  return {
    needsPayment: true,
    paymentStatus: "pending",
    paymentStatusLabel: "Pending enrollment",
    outstanding: pendingCatalogTotal,
    outstandingLabel: formatPkrAmount(pendingCatalogTotal),
    source: "pending_enrollment",
    ledgerStatus,
    ledgerBalance,
    ...base,
    feeCourses: pendingFeeCourses,
    dueMonths: buildPayableDueMonths(registration, pendingFeeCourses),
    chargedCoursesTotal: pendingCatalogTotal,
  };
}
